import {
  Pagination,
  type Selection,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { FiInbox } from 'react-icons/fi';
import { LuLayoutGrid, LuLayoutList } from 'react-icons/lu';
import { Heading } from '@/core/molecules';
import { Button, Spinner } from '@/core/ui';
import {
  type WeeklyRecordReview,
  WeeklyRecordReviewsDocument,
  type WeeklyRecordReviewsQuery,
  type WeeklyRecordReviewsQueryVariables,
} from '@/graphql/gql/graphql';
import { NetworkStatus, useQuery } from '@/libs';
import { useContentReview } from '@/store';
import { FeedbackEditModal } from '../../question/components/feedback-edit-modal';
import { useWeeklyRecordReviewMutations } from './use-weekly-record-review-mutations';
import { WeeklyRecordReviewsGrid } from './weekly-record-reviews-grid';
import { WeeklyReviewTableCell } from './weekly-review-table-cells';
import { WeeklyReviewTableHeaderActions } from './weekly-review-table-header-actions';

const LIMIT = 50;

const COLUMNS: Array<{ name: string; uid: string; sortable: boolean }> = [
  { name: 'SR NO.', uid: 'srNo', sortable: false },
  { name: 'ID', uid: 'id', sortable: false },
  { name: 'REASON', uid: 'reason', sortable: false },
  { name: 'R-ID', uid: 'recordId', sortable: false },
  { name: 'ASSIGNED', uid: 'member', sortable: false },
  { name: 'STATE', uid: 'state', sortable: false },
  { name: 'ACTIONED BY', uid: 'actionedBy', sortable: false },
  { name: 'CREATED AT', uid: 'createdAt', sortable: false },
  { name: 'BY', uid: 'createdBy', sortable: false },
  { name: 'FEEDBACK', uid: 'comment', sortable: false },
  { name: 'ACTIONS', uid: 'actions', sortable: false },
];

type WeeklyRecordReviewsTableProps = {
  filterKey: string;
  search: string;
  createdAtIso: string | undefined;
  createdBy: number | null;
  recordIds: number[];
  type: number | null;
  state: number | null;
  actionedBy: number | null;
  title: string;
};

export const WeeklyRecordReviewsTable: React.FC<WeeklyRecordReviewsTableProps> = ({
  filterKey,
  search,
  createdAtIso,
  createdBy,
  recordIds,
  type,
  state,
  actionedBy,
  title,
}) => {
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');
  const selectedIds = useContentReview.use.selectedIds();
  const setSelectedIds = useContentReview.use.setSelectedIds();
  const {
    handleStateChange,
    handleActionedByChange,
    handleMembersChange,
    handleDelete,
    handleCommentSave,
    deleteLoading,
    updateLoading,
  } = useWeeklyRecordReviewMutations();

  const { data, loading, refetch, networkStatus } = useQuery<
    WeeklyRecordReviewsQuery,
    WeeklyRecordReviewsQueryVariables
  >(WeeklyRecordReviewsDocument, {
    variables: {
      options: {
        page,
        take: LIMIT,
        comment: search || undefined,
        createdAt: createdAtIso ?? undefined,
        memberIds: createdBy != null ? [createdBy] : undefined,
        recordIds: recordIds.length > 0 ? recordIds : undefined,
        type: type ?? undefined,
        state: state ?? undefined,
        actionedBy: actionedBy ?? undefined,
      },
    },
  });

  const isLoading = loading || !data || networkStatus === NetworkStatus.refetch;
  const reviews = (data?.restricted?.weeklyRecordReviews?.result ?? []) as WeeklyRecordReview[];
  const rows = reviews.map((review, index) => ({
    review,
    serialNo: (page - 1) * LIMIT + index + 1,
  }));
  const total = data?.restricted?.weeklyRecordReviews?.total ?? 0;

  useEffect(() => {
    setPage(1);
    refetch();
  }, [filterKey]);

  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      if (keys === 'all') {
        setSelectedIds(reviews.map((item) => item.id as string | number).filter(Boolean));
      } else {
        setSelectedIds(Array.from(keys) as (string | number)[]);
      }
    },
    [reviews, setSelectedIds],
  );

  const renderCell = useCallback(
    (review: WeeklyRecordReview, columnKey: string, serialNo: number) => (
      <WeeklyReviewTableCell
        review={review}
        columnKey={columnKey}
        serialNo={serialNo}
        onStateChange={handleStateChange}
        onActionedByChange={handleActionedByChange}
        onMembersChange={handleMembersChange}
        onDelete={handleDelete}
        deleteLoading={deleteLoading}
      />
    ),
    [handleStateChange, handleMembersChange, handleDelete, deleteLoading],
  );

  return (
    <div className='bg-background rounded-lg shadow-md flex flex-col h-full'>
      <Heading
        description={
          <span className='text-sm font-medium text-warning-600'>
            Showing {reviews.length} reviews on page {page} of {Math.max(1, Math.ceil(total / LIMIT))} (Total:{' '}
            {total})
          </span>
        }
        textClassName='md:text-xl'
        title={title}
        className='md:px-6 py-2'
      >
        <div className='flex items-center gap-2'>
          <Button
            variant={viewMode === 'grid' ? 'solid' : 'bordered'}
            isIconOnly
            size='sm'
            onPress={() => setViewMode('grid')}
            aria-label='Grid view'
          >
            <LuLayoutGrid className='size-4' />
          </Button>
          <Button
            variant={viewMode === 'table' ? 'solid' : 'bordered'}
            isIconOnly
            size='sm'
            onPress={() => setViewMode('table')}
            aria-label='Table view'
          >
            <LuLayoutList className='size-4' />
          </Button>
          <WeeklyReviewTableHeaderActions onRefetch={() => refetch()} isLoading={isLoading} />
        </div>
      </Heading>

      {viewMode === 'grid' ? (
        isLoading ? (
          <div className='flex items-center justify-center min-h-[400px]'>
            <Spinner size='lg' />
          </div>
        ) : rows.length === 0 ? (
          <div className='flex flex-col space-y-4 items-center justify-center min-h-[400px]'>
            <FiInbox className='size-16 text-default-400' />
            <p className='text-default-400 text-2xl font-medium'>No weekly record reviews found</p>
          </div>
        ) : (
          <WeeklyRecordReviewsGrid
            rows={rows}
            selectedIds={new Set(selectedIds as Iterable<string | number>)}
            onSelect={(id, selected) => {
              const next = new Set(selectedIds as Iterable<string | number>);
              if (selected) next.add(id);
              else next.delete(id);
              setSelectedIds(Array.from(next));
            }}
            onStateChange={handleStateChange}
            onActionedByChange={handleActionedByChange}
            onMembersChange={handleMembersChange}
            onDelete={handleDelete}
            deleteLoading={deleteLoading}
          />
        )
      ) : (
        <Table
          isHeaderSticky
          color='default'
          classNames={{
            base: 'max-h-[calc(100vh-400px)] overflow-auto',
            wrapper: 'px-6 scrollbar-thin',
            tr: 'hover:bg-default-100',
            th: [''],
          }}
          isCompact
          shadow='none'
          aria-label='Weekly record reviews table'
          selectionMode='multiple'
          selectedKeys={selectedIds}
          onSelectionChange={handleSelectionChange}
          onRowAction={(_key) => {}}
        >
          <TableHeader columns={COLUMNS}>
            {(column) => (
              <TableColumn
                className='text-sm'
                key={column.uid}
                align={column.uid === 'actions' ? 'center' : 'start'}
              >
                {column.name}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody
            emptyContent={
              <div className='col-span-full flex flex-col space-y-4 items-center justify-center h-full'>
                <FiInbox className='size-16 text-default-400' />
                <p className='text-default-400 text-2xl font-medium'>No weekly record reviews found</p>
              </div>
            }
            isLoading={isLoading}
            loadingContent={<Spinner size='lg' />}
            items={rows}
          >
            {(item) => (
              <TableRow key={item.review.id ?? `row-${item.review.recordId ?? Math.random()}`}>
                {(columnKey) => (
                  <TableCell>{renderCell(item.review, columnKey as string, item.serialNo)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {total > 0 && (
        <div className='p-4 flex justify-between items-center border-t border-default-200'>
          <span className='w-[30%] text-small text-default-400'>
            Page {page} of {Math.max(1, Math.ceil(total / LIMIT))}
          </span>
          <Pagination
            isCompact
            showControls
            showShadow
            color='primary'
            page={page}
            total={Math.max(1, Math.ceil(total / LIMIT))}
            onChange={setPage}
          />
        </div>
      )}

      <FeedbackEditModal onSave={handleCommentSave} isLoading={updateLoading} />
    </div>
  );
};
